import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CallFormDialog from "@/components/CallFormDialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  PhoneCall, Plus, Search, Pencil, Trash2, ArrowRightCircle, PhoneIncoming, PhoneOutgoing,
} from "lucide-react";
import { toast } from "sonner";

const assessmentStyle = (a) => ({
  qualified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-rose-50 text-rose-700 border-rose-200",
  needs_review: "bg-amber-50 text-amber-700 border-amber-200",
}[a] || "bg-gray-50 text-gray-600 border-gray-200");

const statusStyle = (s) => ({
  new: "bg-blue-50 text-blue-700 border-blue-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-rose-50 text-rose-700 border-rose-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
}[s] || "bg-gray-50 text-gray-600 border-gray-200");

export default function Calls() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [meta, setMeta] = useState({});
  const [assessment, setAssessment] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (assessment) params.assessment = assessment;
    if (status) params.status = status;
    if (search.trim()) params.search = search.trim();
    api.get("/calls", { params }).then((r) => setCalls(r.data)).catch(() => {});
  }, [assessment, status, search]);

  useEffect(() => { api.get("/calls/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const confirmDelete = async () => {
    try { await api.delete(`/calls/${deleteTarget.id}`); toast.success("Call deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const confirmConvert = async () => {
    const target = convertTarget;
    setConvertTarget(null);
    try {
      await api.post(`/calls/${target.id}/convert-to-intake`);
      toast.success("Call converted to intake");
      load();
      navigate("/intake");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not convert call");
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="calls-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <PhoneCall className="w-7 h-7 text-[#002FA7]" /> Call Logs
          </h1>
          <p className="text-sm text-gray-500 mt-1">{calls.length} logged call{calls.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-call-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Log call
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="calls-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search caller, phone, reason…"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-sm w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select value={assessment} onChange={(e) => setAssessment(e.target.value)} data-testid="calls-assessment-filter"
          className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="">All assessments</option>
          {(meta.assessments || []).map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="calls-status-filter"
          className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="">All statuses</option>
          {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="calls-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left">
              {["Caller", "Reason", "Source", "Assessment", "Status", "Logged", ""].map((h, i) => (
                <th key={h || `act-${i}`} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No calls logged yet.</td></tr>
            )}
            {calls.map((c) => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`call-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {c.direction === "outbound"
                      ? <PhoneOutgoing className="w-3.5 h-3.5 text-gray-400" />
                      : <PhoneIncoming className="w-3.5 h-3.5 text-gray-400" />}
                    <div>
                      <p className="font-semibold text-gray-900">{c.caller_name || "—"}</p>
                      <p className="text-xs text-gray-500">{c.caller_phone}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700 max-w-[240px] truncate">{c.reason || c.summary || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{c.source}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-sm border capitalize ${assessmentStyle(c.assessment)}`}>
                    {(c.assessment || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-sm border capitalize ${statusStyle(c.status)}`}>{c.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  {c.taken_by && <span className="block text-gray-400">by {c.taken_by}</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {!c.converted_intake_id && (
                      <button onClick={() => setConvertTarget(c)} title="Convert to intake" data-testid={`convert-call-${c.id}`}
                        className="p-1.5 rounded-sm text-gray-400 hover:text-emerald-600 hover:bg-emerald-50">
                        <ArrowRightCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => { setEditing(c); setDialogOpen(true); }} title="Edit" data-testid={`edit-call-${c.id}`}
                      className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => setDeleteTarget(c)} title="Delete" data-testid={`delete-call-${c.id}`}
                        className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CallFormDialog open={dialogOpen} onOpenChange={setDialogOpen} call={editing} meta={meta} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-call-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete call log?</AlertDialogTitle>
            <AlertDialogDescription>This call log will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-call-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(convertTarget)} onOpenChange={(o) => !o && setConvertTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="convert-call-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Convert to intake?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new intake from <strong>{convertTarget?.caller_name || convertTarget?.caller_phone}</strong> and mark the call as converted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmConvert} data-testid="confirm-convert-call-button" className="bg-[#002FA7] hover:bg-[#002480] rounded-sm">Convert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
